<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import Dialog from '@/views/components/Dialog.vue';
import Pagination from '@/views/components/Pagination.vue';
import {DropDownListComponent} from "@syncfusion/ej2-vue-dropdowns";

const router = useRouter();
const store = useFastWay();
const loader = useLoading();
let dialog = ref(null);
let pagination = ref(null);
let gridColumnApi = null;
let loaded = ref(false);
let evaluationAreas = ref([]);
let projects = ref([]);
let data = ref([]);
let totalRowCount = ref(0);
const title = "Áreas de evaluación de proyecto";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Áreas de evaluación de proyecto",
    active: true,
  },
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const linkRenderer = params => {
  return `<a href="/sbg/projects/project_evaluation_areas/${params.data.id}" role="button">${params.value}</a>`;
}

const viewParms = computed({
  get() {
    return store.viewParameters.projectsEvaluationAreasByProyect;
  }
});

const gridHeight = computed({
    get() {
      let height = '600px'
      if (viewParms.value.pageSize < 20) {
        height = `${viewParms.value.pageSize * 29 + 25}px`;
      }
      return height;
    }
});

const years = computed(() => {
  const currentYear = new Date().getFullYear();
  const yearList = [];
  for (let i = 0; i < 5; i++) {
    yearList.push({ id: currentYear - i, name: String(currentYear - i) });
  }
  return yearList;
});

const columnDefs = [
  { headerName: 'ID', field: 'id', width: 80, sortable: true, cellRenderer: linkRenderer},
  { headerName: 'Proyecto', field: 'project_name', width: 250, sortable: true, cellRenderer: linkRenderer},
  { headerName: 'Año', field: 'year', width: 60, sortable: true},
  { headerName: 'Área de Evaluación', field: 'evaluation_area_description', width: 200, sortable: true, cellRenderer: linkRenderer},
  { headerName: 'Descripción', field: 'description', flex: 1, minWidth: 150, sortable: true, cellRenderer: linkRenderer},
  { headerName: 'Resumen', field: 'summary', width: 110, sortable: true, cellRenderer: linkRenderer},
  { headerName: 'Secuencia', field: 'sequence', width: 115, sortable: true}
];

onMounted(async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando catálogos'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    order: 'name',
    statusId: 'ACT'
  };
  await axios
  .post(`${api.value}/sbg/projects/list`, params)
  .then(response => {
    if (response.data.success) {
      projects.value = response.data.data;
    } else {
      dialog.value.show('error', 'Error al cargar de proyectos', response.data.message);
    }
    const evalParams = {
      userId: store.userInfo.userId,
      token: store.userInfo.token,
      order: 'description',
      context: store.userInfo
    };
    axios
    .post(`${api.value}/sbg/projects/evaluation_areas/list`, evalParams)
    .then(evalResponse => {
      if (evalResponse.data.success) {
        evaluationAreas.value = evalResponse.data.data;
      }
      getData();
      loading.hide();
    })
    .catch(evalError => {
      let errorMessage = '';
      if (evalError.message) {
        errorMessage = evalError.message;
      } else if (evalError.response) {
        errorMessage = evalError.response.data.ErrorMessage;
      } else {
        errorMessage = 'Error de conectividad al cargar áreas de evaluación';
      }
      loading.hide();
      dialog.value.show('error', 'Error al cargar áreas de evaluación', errorMessage);
    });
  })
  .catch(error => {
      let errorMessage = '';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response) {
        errorMessage = error.response.data.ErrorMessage;
      } else {
        errorMessage = 'Error de conectividad al cargar catálogo';
      }
      loading.hide();
      dialog.value.show('error', 'Error al cargar catálogos', errorMessage);
  });
});

const getData = async () => {
  data.value = [];
  totalRowCount.value = 0;
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando áreas de evaluación de proyecto'});
  const params = {
    context: {
      userId: store.userInfo.userId,
      login: store.userInfo.login || store.userInfo.userName || '',
      token: store.userInfo.token,
      dbName: store.userInfo.organizationName || '',
      limit: viewParms.value.pageSize,
      startPage: viewParms.value.currentPage,
      searchPhrase: viewParms.value.search,
      order: viewParms.value.order,
      orderDirection: viewParms.value.orderDirection
    },
    projectId: viewParms.value.projectId,
    year: viewParms.value.year,
    evaluationAreaId: viewParms.value.evaluationAreaId
  };
  await axios
  .post(`${api.value}/sbg/projects/project_evaluation_areas/list`, params)
  .then(response => {
    if (response.data.success) {
      if (response.data.data) {
        data.value = response.data.data;
        if (response.data.data.length > 0) {
          totalRowCount.value = response.data.data[0].total_row_count;
        }
      }
      if (gridColumnApi && viewParms.value.order != '') {
        gridColumnApi.api.applyColumnState({
          state: [{ colId: viewParms.value.order, sort: viewParms.value.orderDirection }],
          defaultState: { sort: null }
        });
      }
    } else {
      dialog.value.show('error', 'Error interno al cargar áreas de evaluación de proyecto', response.data.message);
    }
    pagination.value.setTotalItems(totalRowCount.value);
    loading.hide();
    loaded.value = true;
  })
  .catch(error => {
    loaded.value = true;
    loading.hide();
    dialog.value.show('error', 'Error al cargar áreas de evaluación de proyecto', error.message);
  });
}

const onFilterChange = () => {
  if (!loaded.value) {
    return;
  }
  viewParms.value.currentPage = 1;
  getData();
}

const clearSearchPhrase = () => {
    viewParms.value.currentPage = 1;
    viewParms.value.search = '';
    searchPhrase();
}

const searchPhrase = () => {
    if (loaded.value) {
      viewParms.value.currentPage = 1;
      getData();
    }
}

const postSortRows = params => {
  let sortChanged = false;
  for (const col of params.api.getColumnState()) {
    if (col.sort) {
      if (viewParms.value.order != col.colId) {
        viewParms.value.order = col.colId;
        sortChanged = true;
      }
      if (viewParms.value.orderDirection != col.sort) {
        viewParms.value.orderDirection = col.sort;
        sortChanged = true;
      }
    }
  }
  if (sortChanged && loaded.value) {
    getData();
  }
}

const pageSizeChange = pageSize => {
  viewParms.value.currentPage = 1;
  viewParms.value.pageSize = pageSize;
  getData();
}

const setPage = page => {
  viewParms.value.currentPage = page;
  getData();
}

const onGridReady = params => {
  gridColumnApi = params.columnApi;
}

const add = () => {
  router.push({
    name: 'projectsProjectEvaluationArea',
    params: {id: 0}
  });
}

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-end align-middle row mb-2">
                        <div class="col d-flex justify-content-start">
                          <b-button variant="outline-success" class="btn-icon waves-effect waves-light me-1" v-b-tooltip.hover title="Recargar" @click="getData">
                              <i class="las la-sync"></i>
                          </b-button>
                          <b-button variant="success" class="btn-icon waves-effect waves-light" v-b-tooltip.hover title="Agregar" @click="add">
                              <i class="las la-plus"></i>
                          </b-button>
                        </div>
                        <div class="col"/>
                        <div class="d-inline-block mr-1" style="width: 320px;">
                          <DropDownListComponent v-model:value="viewParms.projectId" floatLabelType="Auto" placeholder="Proyecto" highlight=true :dataSource="projects" :fields="{text: 'name', value: 'id'}" :showClearButton='true' :allowFiltering="true" filterType="Contains" :filterBarPlaceholder="'Buscar proyecto'" :change="onFilterChange"/>
                        </div>
                        <div class="d-inline-block mr-1" style="width: 90px;">
                          <DropDownListComponent v-model:value="viewParms.year" floatLabelType="Auto" placeholder="Año" :dataSource="years" :fields="{text: 'name', value: 'id'}" :showClearButton='false' :allowFiltering="false" :change="onFilterChange"/>
                        </div>
                        <div class="d-inline-block mr-1" style="width: 260px;">
                          <DropDownListComponent v-model:value="viewParms.evaluationAreaId" floatLabelType="Auto" placeholder="Área de evaluación" highlight=true :dataSource="evaluationAreas" :fields="{text: 'description', value: 'id'}" :showClearButton='true' :allowFiltering="true" filterType="Contains" :filterBarPlaceholder="'Buscar área'" :change="onFilterChange"/>
                        </div>
                        <div class="d-inline-block" style="width: 240px;">
                          <div class="e-input-group">
                            <input type="text" v-model="viewParms.search" class="e-input" showClearButton="true" placeholder="Buscar" @keyup.enter="searchPhrase"/>
                            <span v-if="viewParms.search != ''" class="e-input-group-icon e-input-clear" @click="clearSearchPhrase"/>
                            <span class="e-input-group-icon e-input-search" @click="searchPhrase"/>
                          </div>
                        </div>
                    </div>
                    <ag-grid-vue
                        style="width: 100%;" :style="{height: gridHeight}"
                        class="ag-theme-balham"
                        :columnDefs="columnDefs"
                        :rowData="data"
                        :accentedSort="true"
                        :postSortRows="postSortRows"
                        @grid-ready="onGridReady"
                    >
                    </ag-grid-vue>
                    <Pagination ref="pagination" class="mt-2" :pageSize="viewParms.pageSize" :currentPage="viewParms.currentPage" @pageSizeChange="pageSizeChange" @pageChange="setPage"/>
                </div>
            </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";

#input-container .e-input-group {
  margin: 30px 0;
}

.e-input-group-icon:before {
  font-family: e-icons;
}

.e-input-group .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group.e-small .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group-icon.e-input-clear:before {
  content: "\e7a7";
}

.e-input-group-icon.e-input-search:before {
  content: "\e993";
}
</style>
